import {
  enumCameraStatus,
  enumPrinterStatus,
  enumScannerStatus,
} from '@rematter/frontend-lib-api-client';

const customAttributesView = {
  entityTypes: 'Entity types',
  entityTypeLabels: {
    CoreOrganization: 'Organization',
    CoreUser: 'User',
    CoreFacility: 'Facility',
    CoreFacilityGroup: 'Facility Group',
    CRMAccount: 'Account',
    CRMContact: 'Contact',
    CRMAccountAddress: 'Account Address',
    InventoryCategory: 'Category',
    InventoryItemGroup: 'Item Group',
    InventoryItem: 'Item',
    InventoryTag: 'Tag',
    FilesFile: 'File',
    FinanceFinancialAccount: 'Financial Account',
    SalesSalesOrder: 'Sales Order',
  },
  addCustomAttribute: 'Add custom attribute',
  creationDialog: {
    title: 'Create Custom Attribute',
    fields: {
      name: 'Name',
      dataType: 'Data Type',
      options: 'Options',
      addOption: 'Add option',
      objectType: 'Object Type',
      constraints: 'Constraints',
      allowMultipleValues: 'Allow multiple values',
      required: 'Required',
      unique: 'Unique',
      minLength: 'Min Length',
      maxLength: 'Max Length',
      minValue: 'Min Value',
      maxValue: 'Max Value',
    },
    save: 'Save',
  },
  cards: {
    actions: {
      edit: 'Edit',
      confirmDeletion: 'Confirm deletion',
    },
    badges: {
      minLength: 'Min length',
      maxLength: 'Max length',
      minValue: 'Min value',
      maxValue: 'Max value',
      unique: 'Unique',
      multipleValues: 'Multiple values',
      required: 'Required',
    },
  },
};

const jsonView = {
  title: 'Settings Data',
  subtitle:
    'Raw JSON representation of all settings groups, settings, and values',
  toolbar: {
    refresh: 'Refresh',
    search: 'Search settings groups...',
  },
  content: {
    loading: 'Loading settings data...',
    error: 'Error loading settings data',
    noData: 'No settings data found',
    groupsCount: (count: number) => `${count} groups`,
  },
  actions: {
    retry: 'Retry',
  },
};

const settingsView = {
  groups: 'Settings Groups',
  selectGroup: 'Select a settings group to view settings',
  organizationValue: 'Organization Value',
  scopes: 'Available Scopes',
  overrides: 'Overrides',
  addOverride: 'Add Override',
  noOverrides: 'No overrides configured',
  selectScope: 'Select scope',
  selectEntity: 'Select entity',
  discardChanges: 'Discard Changes',
  saveSettings: 'Save Settings',
};

const teamAndPermissions = {
  title: 'Team Management',
  subtitle: 'Add & manage team members and access',
  noPermission: "You don't have permission to access this section.",
  tabs: {
    facilities: 'Facilities',
    roles: 'Roles',
    users: 'Users',
  },
  search: {
    placeholder: 'Search',
  },
  emptyState: {
    facilities: 'No facilities found',
    roles: 'No roles found',
    users: 'No users found',
  },
  actions: {
    addRole: 'Add Role',
    addUser: 'Add User',
    allUsersAdded: 'All users have been added',
    discard: 'Discard',
    save: 'Save Updates',
  },
  addUserDialog: {
    title: 'Add User',
    steps: {
      userInfo: 'User Info',
      roleAssignment: 'Roles',
    },
    fields: {
      firstName: {
        label: 'First Name',
        placeholder: 'First name',
      },
      lastName: {
        label: 'Last Name',
        placeholder: 'Last name',
      },
      email: {
        label: 'Email',
        placeholder: 'Email address',
      },
      phoneNumber: {
        label: 'Phone',
        placeholder: '+1 (555) 000-0000',
      },
    },
    roleStep: {
      searchRolesPlaceholder: 'Search roles...',
      searchFacilitiesPlaceholder: 'Search facilities...',
      rolesHeader: 'Roles',
      facilitiesHeader: 'Facilities',
      selectedAssignments: 'Selected Assignments',
      noRolesFound: 'No roles found',
      noFacilitiesFound: 'No facilities found',
      assignmentCount: (count: number) =>
        `${count} assignment${count !== 1 ? 's' : ''}`,
      selectRolePrompt: 'Select a role to assign facilities',
      loading: 'Loading...',
      clearButton: 'Clear',
    },
    actions: {
      cancel: 'Cancel',
      sendInvite: 'Send Invite',
      next: 'Next',
      back: 'Back',
    },
    messages: {
      success: 'Invite sent successfully',
      error: 'Failed to send invite',
    },
  },
  facilities: {
    table: {
      columns: {
        name: 'Facility Name',
        address: 'Address',
        users: 'Users',
        roles: 'Active Roles',
      },
      aria: 'Facilities table',
    },
    emptyState: {
      title: 'No facilities found',
      subtitle: 'There are no facilities matching your search criteria.',
    },
    details: {
      tabs: {
        roles: 'Roles',
        users: 'Users',
      },
      actions: {
        goToRoleSettings: 'Go to Role Settings',
        goToUserSettings: 'Go to User Settings',
      },
      sections: {
        users: 'Users',
        roles: 'Roles',
        permissions: 'Permissions',
      },
      roles: {
        emptyState: 'Select a role',
        emptyStateSubtitle:
          'Choose a role from the list to view users and permissions at this facility',
      },
      users: {
        columns: {
          name: 'Name',
          email: 'Email',
          phone: 'Phone',
        },
        emptyState: {
          title: 'No users at this facility',
          subtitle:
            'Users assigned to roles at this facility will appear here.',
        },
        selectEmptyState: 'Select a user',
        selectEmptyStateSubtitle:
          'Choose a user from the list to view their roles and permissions at this facility',
        removeAccess: 'Remove Access',
        rolesUpdated: 'User roles updated successfully',
        rolesUpdateError: 'Failed to update user roles',
        removeFromFacilityDialog: {
          title: (name: string) => `Remove ${name} from this facility?`,
          description: (name: string) =>
            `After confirming, ${name} will be removed from this facility. This action can be undone by reassigning the user.`,
          confirm: 'Remove',
          cancel: 'Cancel',
          success: 'User removed from facility',
          error: 'Failed to remove user from facility',
        },
      },
    },
  },
  roles: {
    table: {
      columns: {
        name: 'Role Name',
        users: 'Users',
        status: 'Status',
        actions: 'Actions',
      },
      actions: {
        archiveRole: 'Archive role',
        restoreRole: 'Restore role',
      },
      showArchived: 'Show archived',
      aria: 'Roles table',
    },
    emptyState: {
      title: 'No roles found',
      subtitle: 'There are no roles matching your search criteria.',
    },
    details: {
      tabs: {
        permissions: 'Permissions',
        users: 'Users',
      },
      actions: {
        rename: 'Rename',
        archive: 'Archive',
        restore: 'Restore',
      },
      users: {
        viewUserSettings: 'View User Settings',
        removeFromRole: 'Remove from Role',
        removeFromRoleDialog: {
          title: (name: string) => `Remove ${name} from this role?`,
          description: (name: string) =>
            `After confirming, ${name} will be removed from this role. This action can be undone by reassigning the role.`,
          confirm: 'Remove',
          cancel: 'Cancel',
          success: 'User removed from role',
          error: 'Failed to remove user from role',
        },
        bulk: {
          selected: 'selected',
          removeFromRole: 'Remove from Role',
          resendInvite: 'Resend Invite',
          bulkRemoveDialog: {
            title: (count: number) =>
              `Remove ${count} user${count === 1 ? '' : 's'} from this role?`,
            description: (count: number) =>
              `After confirming, ${count} user${count === 1 ? '' : 's'} will be removed from this role. This action can be undone by reassigning the role.`,
            confirm: 'Remove',
            cancel: 'Cancel',
          },
          removeSuccess: (count: number) =>
            `${count} user${count === 1 ? '' : 's'} removed from role`,
          removeError: 'Failed to remove users from role',
        },
      },
    },
    renameDialog: {
      title: 'Rename Role',
      label: 'Role Name',
      placeholder: 'Enter role name',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Role renamed successfully',
      error: 'Failed to rename role',
    },
    addRoleDialog: {
      title: 'Create Role',
      fields: {
        name: {
          label: 'Role Name',
          placeholder: 'Enter role name',
        },
      },
      actions: {
        cancel: 'Cancel',
        save: 'Save Role',
      },
      messages: {
        success: 'Role created successfully',
        error: 'Failed to create role',
      },
    },
    archivedReadOnlyMessage:
      "Archived roles can't be edited. Restore the role to make changes.",
    dialogs: {
      archive: {
        title: (name: string) => `Archive ${name}?`,
        description: (name: string) =>
          `After confirming, the role "${name}" will be archived. Users assigned to this role will lose the associated permissions. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Archive',
        success: (name: string) => `${name} role has been archived`,
      },
      restore: {
        title: (name: string) => `Restore ${name}?`,
        description: (name: string) =>
          `After confirming, the role "${name}" will be restored and become active again. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Restore',
        success: (name: string) => `${name} role has been restored`,
      },
      bulkArchive: {
        title: (count: number) =>
          `Archive ${count} role${count === 1 ? '' : 's'}?`,
        description: (count: number) =>
          `After confirming, ${count} role${count === 1 ? '' : 's'} will be archived. Users assigned to these roles will lose the associated permissions. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Archive',
        success: (count: number) =>
          `${count} role${count === 1 ? '' : 's'} archived`,
      },
      bulkRestore: {
        title: (count: number) =>
          `Restore ${count} role${count === 1 ? '' : 's'}?`,
        description: (count: number) =>
          `After confirming, ${count} role${count === 1 ? '' : 's'} will be restored and become active again. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Restore',
        success: (count: number) =>
          `${count} role${count === 1 ? '' : 's'} restored`,
      },
    },
    bulk: {
      selected: 'selected',
      archive: 'Archive',
      restore: 'Restore',
    },
    addUsersDialog: {
      title: 'Add User(s) to {roleName} Role',
      steps: {
        users: 'Users',
        facilities: 'Facilities',
      },
      users: {
        header: 'USERS',
        searchPlaceholder: 'Search',
      },
      facilities: {
        header: 'FACILITIES',
        searchPlaceholder: 'Search',
      },
      actions: {
        clear: 'Clear',
        next: 'Next',
        back: 'Back',
        save: 'Save',
        addUser: 'Add User ({count})',
      },
      messages: {
        success: 'Users added to role successfully',
        error: 'Failed to add users to role',
        partialSuccess:
          '{successCount} of {totalCount} users added successfully',
      },
    },
  },
  userStatus: {
    active: 'Active',
    archived: 'Archived',
    inviteSent: 'Invite Sent',
  },
  roleStatus: {
    active: 'Active',
    archived: 'Archived',
  },
  users: {
    selected: 'selected',
    table: {
      columns: {
        name: 'Name',
        contactInfo: 'Contact Info',
        facilities: 'Facilities',
        status: 'Status',
      },
      aria: 'Users table',
    },
    filters: {
      allFacilities: 'All Facilities',
      allRoles: 'All Roles',
    },
    actions: {
      edit: 'Edit',
      resendInvite: 'Resend Invite',
      removeAccess: 'Remove Access',
      restoreAccess: 'Restore Access',
      deleteUser: 'Delete User',
    },
    emptyState: {
      title: 'No users found',
      subtitle: 'There are no users matching your search criteria.',
    },
    dialogs: {
      removeAccess: {
        title: (name: string) => `Remove access for ${name}?`,
        description: (name: string) =>
          `After confirming, ${name} will lose access to the system. Do you want to proceed?`,
        discard: 'Discard',
        remove: 'Remove',
      },
      restoreAccess: {
        title: (name: string) => `Restore access for ${name}?`,
        description: (name: string) =>
          `After confirming, access for ${name} with all assigned permissions will be restored. Do you want to proceed?`,
        discard: 'Discard',
        restore: 'Restore',
      },
      deleteAccount: {
        title: (name: string) => `Delete ${name}?`,
        description: (name: string) =>
          `After confirming, ${name} will be deleted and no longer exist in the app. To confirm delete action fill the team member name.`,
        discard: 'Discard',
        delete: 'Delete',
        teamMemberNameLabel: 'Team member name',
      },
    },
  },
  userDetails: {
    loading: 'Loading user...',
    contactInfo: {
      sectionTitle: 'Contact Info',
      fields: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        phone: 'Phone',
      },
      accessCode: {
        sectionTitle: 'Access Code',
        subtitle: 'Used as a fallback password to log into the system',
        codeSentToPrefix: 'The code will be sent to',
        codeSentToOr: 'or',
        codeLabel: 'Code',
        sendViaEmail: 'Send via Email',
        sendViaText: 'Send via Text',
      },
      messages: {
        updateSuccess: 'User updated successfully',
        updateError: 'Failed to update user',
        codeRegenerated: 'Access code regenerated',
        codeRegenerateError: 'Failed to regenerate access code',
        codeSentViaEmail: 'Access code sent via email',
        codeSentViaText: 'Access code sent via text',
      },
    },
    roles: {
      searchFacilitiesAriaLabel: 'Search facilities',
      allFacilities: (count: number) => `All Facilities (${count})`,
      assignFacilityAriaLabel: (name: string) => `Assign ${name}`,
      selectAllFacilitiesAriaLabel: 'Select all facilities',
      lastUpdated: (date: string, user: string) =>
        `Last updated on ${date} by ${user}`,
      emptyState: {
        title: 'Select a role',
        subtitle: 'Choose a role from the list to assign facilities',
      },
      messages: {
        updateSuccess: 'Role facilities updated successfully',
        updateError: 'Failed to update role facilities',
      },
    },
    facilities: {
      searchPlaceholder: 'Search facilities...',
      rolesLabel: 'Role(s)',
      selectRolesPlaceholder: 'Select roles',
      permissionsSet: 'Permissions Set',
      noPermissions: 'No Permissions',
      lastUpdated: (date: string, user: string) =>
        `Last updated on ${date} by ${user}`,
      noUpdatesYet: 'No updates yet',
      permissionInherited: (roleNames: string) =>
        `Permission is inherited from ${roleNames} and cannot be changed per user`,
      noRolesAssigned: 'No roles assigned. Assign roles to grant permissions.',
      emptyState: {
        title: 'Select a facility',
        subtitle: 'Choose a facility from the list to view or edit permissions',
      },
      messages: {
        updateSuccess: 'Facility permissions updated successfully',
        updateError: 'Failed to update facility permissions',
      },
    },
  },
};

const inventoryView = {
  common: {
    cancel: 'Cancel',
    confirmDelete: 'Delete',
  },
  sidebar: {
    title: 'Inventory',
  },
  sections: {
    items: 'Items',
    itemAliases: 'Item Aliases',
    categories: 'Categories',
    itemGroups: 'Item Groups',
  },
  itemsPanel: {
    columns: {
      name: 'Name',
      sku: 'SKU',
      category: 'Category',
      itemGroup: 'Item Group',
      description: 'Description',
      created: 'Created',
    },
    search: {
      placeholder: 'Search items...',
      ariaLabel: 'Search items',
    },
    emptyState: {
      title: 'No items found',
      description: 'Create items to manage your inventory.',
    },
    actions: {
      create: 'Create Item',
    },
    errors: {
      loadingFailed: (message: string) => `Error loading items: ${message}`,
    },
    ariaLabel: 'Items list',
  },
  itemGroupsPanel: {
    columns: {
      name: 'Name',
      number: 'Number',
      parentGroup: 'Parent Group',
      description: 'Description',
      created: 'Created',
    },
    search: {
      placeholder: 'Search item groups...',
      ariaLabel: 'Search item groups',
    },
    emptyState: {
      title: 'No item groups found',
      description:
        'Create item groups to organize your inventory items hierarchically.',
    },
    actions: {
      create: 'Create Item Group',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error loading item groups: ${message}`,
    },
    ariaLabel: 'Item groups list',
  },
  categoriesPanel: {
    columns: {
      name: 'Name',
      description: 'Description',
      attributes: 'Attributes',
      created: 'Created',
    },
    search: {
      placeholder: 'Search categories...',
      ariaLabel: 'Search categories',
    },
    emptyState: {
      title: 'No categories found',
      description:
        'Create categories to classify your inventory items with custom attributes.',
    },
    actions: {
      create: 'Create Category',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error loading categories: ${message}`,
    },
    badges: {
      attribute: 'attribute',
      attributes: 'attributes',
    },
    ariaLabel: 'Categories list',
  },
  itemAliasesPanel: {
    columns: {
      item: 'Item (Master)',
      aliasName: 'Alias Name',
      aliasCode: 'Alias Code',
      account: 'Account',
      type: 'Type',
      notes: 'Notes',
      created: 'Created',
    },
    search: {
      placeholder: 'Search item aliases...',
      ariaLabel: 'Search item aliases',
    },
    emptyState: {
      title: 'No item aliases found',
      description:
        'Create item aliases to define per-account naming for items.',
    },
    actions: {
      create: 'Create Item Alias',
    },
    errors: {
      loadingFailed: (message: string) =>
        `Error loading item aliases: ${message}`,
    },
    ariaLabel: 'Item aliases list',
  },
  itemCreateDialog: {
    title: 'Create Item',
    sections: {
      details: 'Item Details',
      additional: 'Additional Information',
    },
    fields: {
      name: 'Name',
      skuNumber: 'SKU Number',
      category: 'Category',
      itemGroup: 'Item Group',
      pricingUnitOverride: 'Pricing Unit Override',
      description: 'Description',
      notes: 'Notes',
    },
    placeholders: {
      name: 'e.g., Recycled Aluminum Scrap',
      skuNumber: 'e.g., ALUM-001',
      category: 'Select category...',
      itemGroup: 'Select item group...',
      pricingUnitOverride: 'None (inherit from group)',
      description: 'Optional description...',
      notes: 'Optional notes...',
    },
    validation: {
      nameRequired: 'Please enter a name',
      skuRequired: 'Please enter a SKU number',
      categoryRequired: 'Please select a category',
      itemGroupRequired: 'Please select an item group',
    },
    messages: {
      success: 'Item created successfully',
      error: (message: string) => `Failed to create item: ${message}`,
    },
    actions: {
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Item',
    },
  },
  itemGroupCreateDialog: {
    title: 'Create Item Group',
    sections: {
      details: 'Item Group Details',
      additional: 'Additional Information',
    },
    fields: {
      name: 'Name',
      number: 'Number',
      parentGroup: 'Parent Group',
      defaultPricingUnit: 'Default Pricing Unit',
      description: 'Description',
      notes: 'Notes',
    },
    placeholders: {
      name: 'e.g., Aluminum Products',
      number: 'e.g., ALU-GRP',
      parentGroup: 'Select parent group (optional)...',
      defaultPricingUnit: 'None (defaults to Pounds)',
      description: 'Optional description...',
      notes: 'Optional notes...',
    },
    validation: {
      nameRequired: 'Please enter a name',
      numberRequired: 'Please enter a number',
    },
    messages: {
      success: 'Item group created successfully',
      error: (message: string) => `Failed to create item group: ${message}`,
    },
    actions: {
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Item Group',
    },
  },
  categoryCreateDialog: {
    title: 'Create Category',
    sections: {
      details: 'Category Details',
      attributes: 'Attributes',
    },
    fields: {
      name: 'Name',
      description: 'Description',
      attributeName: 'Attribute Name',
      dataType: 'Data Type',
      options: 'Options (comma-separated)',
    },
    placeholders: {
      name: 'e.g., Ferrous Metals',
      description: 'Optional description...',
      attributeName: 'e.g., Grade',
      attributeDescription: 'Optional description...',
      options: 'e.g., Option A, Option B, Option C',
    },
    validation: {
      nameRequired: 'Please enter a name',
      attributeNameRequired: 'Please enter a name for all attributes',
    },
    messages: {
      success: 'Category created successfully',
      error: (message: string) => `Failed to create category: ${message}`,
      noAttributes:
        'No attributes defined. Add attributes to define custom fields for items in this category.',
    },
    attributes: {
      required: 'Required',
      removeAriaLabel: 'Remove attribute',
    },
    actions: {
      addAttribute: 'Add Attribute',
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Category',
    },
  },
  itemEditDialog: {
    title: 'Edit Item',
    sections: {
      details: 'Item Details',
      additional: 'Additional Information',
    },
    fields: {
      name: 'Name',
      skuNumber: 'SKU Number',
      category: 'Category',
      itemGroup: 'Item Group',
      pricingUnitOverride: 'Pricing Unit Override',
      description: 'Description',
      notes: 'Notes',
    },
    placeholders: {
      name: 'e.g., Recycled Aluminum Scrap',
      skuNumber: 'e.g., ALUM-001',
      category: 'Select category...',
      itemGroup: 'Select item group...',
      pricingUnitOverride: 'None (inherit from group)',
      description: 'Optional description...',
      notes: 'Optional notes...',
    },
    validation: {
      nameRequired: 'Please enter a name',
      skuRequired: 'Please enter a SKU number',
      categoryRequired: 'Please select a category',
      itemGroupRequired: 'Please select an item group',
    },
    messages: {
      success: 'Item updated successfully',
      error: (message: string) => `Failed to update item: ${message}`,
    },
    actions: {
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save Changes',
    },
  },
  itemDetailView: {
    sections: {
      itemInformation: 'Item Information',
      timestamps: 'Timestamps',
      description: 'Description',
      notes: 'Notes',
      attributes: 'Attributes',
    },
    fields: {
      sku: 'SKU',
      category: 'Category',
      itemGroup: 'Item Group',
      created: 'Created',
      updated: 'Updated',
    },
    tabs: {
      details: 'Details',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToItems: 'Back to Items',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this item? This action cannot be undone.',
      deleteSuccess: 'Item deleted successfully',
      deleteError: (message: string) => `Failed to delete item: ${message}`,
      itemNotFound: 'Item not found',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Item updated successfully',
      updateError: (message: string) => `Failed to update item: ${message}`,
    },
  },
  itemAliasCreateDialog: {
    title: 'Create Item Alias',
    sections: {
      details: 'Alias Details',
      additional: 'Additional Information',
    },
    fields: {
      item: 'Item',
      account: 'Account',
      aliasName: 'Alias Name',
      aliasCode: 'Alias Code',
      type: 'Type',
      notes: 'Notes',
    },
    placeholders: {
      item: 'Select an item...',
      account: 'Select an account...',
      aliasName: 'What the account calls this item...',
      aliasCode: 'Optional code...',
      notes: 'Optional notes...',
    },
    aliasTypes: {
      buy: 'Buy (from suppliers)',
      sell: 'Sell (to customers)',
    },
    validation: {
      itemRequired: 'Please select an item',
      accountRequired: 'Please select an account',
      aliasNameRequired: 'Please enter an alias name',
    },
    messages: {
      success: 'Item alias created successfully',
      error: (message: string) => `Failed to create item alias: ${message}`,
    },
    actions: {
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Item Alias',
    },
  },
  itemGroupDetailView: {
    sections: {
      itemGroupInformation: 'Item Group Information',
      timestamps: 'Timestamps',
      additional: 'Additional Information',
    },
    fields: {
      number: 'Number',
      parentGroup: 'Parent Group',
      created: 'Created',
      updated: 'Updated',
    },
    tabs: {
      details: 'Details',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToItemGroups: 'Back to Item Groups',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this item group? This action cannot be undone.',
      deleteSuccess: 'Item group deleted successfully',
      deleteError: (message: string) =>
        `Failed to delete item group: ${message}`,
      itemGroupNotFound: 'Item group not found',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Item group updated successfully',
      updateError: (message: string) =>
        `Failed to update item group: ${message}`,
    },
  },
  itemAliasDetailView: {
    sections: {
      aliasAssociation: 'Alias Association',
      aliasInformation: 'Alias Information',
      timestamps: 'Timestamps',
      additional: 'Additional Information',
    },
    fields: {
      created: 'Created',
      updated: 'Updated',
    },
    tabs: {
      details: 'Details',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToItemAliases: 'Back to Item Aliases',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this item alias? This action cannot be undone.',
      deleteSuccess: 'Item alias deleted successfully',
      deleteError: (message: string) =>
        `Failed to delete item alias: ${message}`,
      itemAliasNotFound: 'Item alias not found',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Item alias updated successfully',
      updateError: (message: string) =>
        `Failed to update item alias: ${message}`,
    },
  },
  categoryDetailView: {
    sections: {
      categoryInformation: 'Category Information',
      timestamps: 'Timestamps',
      attributes: 'Attributes',
    },
    fields: {
      created: 'Created',
      updated: 'Updated',
    },
    tabs: {
      details: 'Details',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToCategories: 'Back to Categories',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this category? This action cannot be undone.',
      deleteSuccess: 'Category deleted successfully',
      deleteError: (message: string) => `Failed to delete category: ${message}`,
      categoryNotFound: 'Category not found',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Category updated successfully',
      updateError: (message: string) => `Failed to update category: ${message}`,
    },
  },
};

const complianceView = {
  common: {
    actions: {
      edit: 'Edit',
      delete: 'Delete',
      viewDetails: 'View Details',
      cancel: 'Cancel',
    },
    confirmDelete: 'Are you sure?',
    yesDelete: 'Yes, delete',
    yes: 'Yes',
    no: 'No',
    loading: 'Loading...',
  },
  rules: {
    title: 'Rule Configuration',
    subtitle: 'Configure compliance rules per facility',
    facilityList: {
      columns: {
        name: 'Facility',
        type: 'Type',
        activeRules: 'Active Rules',
      },
      search: {
        placeholder: 'Search facilities...',
      },
      emptyState: {
        title: 'No facilities found',
        description: 'Create facilities in Locations settings first.',
      },
    },
    facilityRules: {
      title: 'Facility Rules',
      subtitle: 'Manage compliance rules for this facility',
      columns: {
        rule: 'Rule',
        category: 'Category',
        enforcement: 'Enforcement',
        value: 'Value',
        enabled: 'Enabled',
      },
      actions: {
        addRule: 'Add Rule',
        applyJurisdictionPack: 'Apply Jurisdiction Pack',
      },
      emptyState: {
        title: 'No rules configured',
        description:
          'Add rules individually or apply a jurisdiction pack to get started.',
      },
    },
    dialog: {
      createTitle: 'Add Facility Rule',
      editTitle: 'Edit Facility Rule',
      sections: {
        rule: 'Rule',
      },
      fields: {
        ruleDefinition: 'Rule Definition',
        isEnabled: 'Enabled',
        configuredValue: 'Value',
        enforcementLevelOverride: 'Enforcement Level',
        notes: 'Notes',
      },
      placeholders: {
        ruleDefinition: 'Select a rule...',
        notes: 'Optional notes...',
        booleanValue: 'true / false',
        integerValue: 'e.g., 7',
        decimalValue: 'e.g., 25.00',
        defaultValue: 'Value...',
        enforcementLevel: 'Use Default',
      },
      ruleInfo: {
        type: 'Type',
        enforcement: 'Enforcement',
      },
      actions: {
        create: 'Add Rule',
        save: 'Save Changes',
      },
      messages: {
        createSuccess: 'Rule added successfully',
        createError: (message: string) => `Failed to add rule: ${message}`,
        updateSuccess: 'Rule updated successfully',
        updateError: (message: string) => `Failed to update rule: ${message}`,
        deleteSuccess: 'Rule removed successfully',
        deleteError: (message: string) => `Failed to remove rule: ${message}`,
      },
    },
    jurisdictionPacks: {
      title: 'Apply Jurisdiction Pack',
      description:
        'Apply a pre-configured set of rules for a specific jurisdiction.',
      jurisdictions: {
        CA: 'California',
        TX: 'Texas',
        FL: 'Florida',
        OH: 'Ohio',
        DEFAULT: 'Default (US)',
      },
      actions: {
        apply: 'Apply Pack',
      },
      messages: {
        applySuccess: (jurisdiction: string) =>
          `${jurisdiction} jurisdiction pack applied successfully`,
        applyError: (message: string) =>
          `Failed to apply jurisdiction pack: ${message}`,
        noMatchingRules:
          'No matching rule definitions found for this jurisdiction pack',
      },
    },
  },
  doNotBuy: {
    title: 'Do-Not-Buy List',
    subtitle: 'Manage blocked sellers',
    columns: {
      account: 'Account',
      reason: 'Reason',
      status: 'Status',
      addedBy: 'Added By',
      effectiveFrom: 'Effective From',
    },
    search: {
      placeholder: 'Search entries...',
    },
    emptyState: {
      title: 'No do-not-buy entries',
      description: 'Add accounts to prevent transactions with blocked sellers.',
    },
    actions: {
      addEntry: 'Add Entry',
    },
    dialog: {
      createTitle: 'Add Do-Not-Buy Entry',
      editTitle: 'Edit Do-Not-Buy Entry',
      sections: {
        entryDetails: 'Entry Details',
      },
      fields: {
        account: 'Account',
        facility: 'Facility (Optional)',
        reason: 'Reason',
        originType: 'Origin Type',
        effectiveFrom: 'Effective From',
        effectiveTo: 'Effective To (Optional)',
        notes: 'Notes',
      },
      placeholders: {
        account: 'Search accounts...',
        facility: 'All facilities',
        reason: 'Reason for blocking...',
        originType: 'Select origin type...',
        notes: 'Optional notes...',
      },
      originTypes: {
        Manual: 'Manual',
        Automated: 'Automated',
        LawEnforcement: 'Law Enforcement',
      },
      actions: {
        create: 'Add Entry',
        save: 'Save Changes',
      },
      messages: {
        createSuccess: 'Do-not-buy entry created successfully',
        createError: (message: string) => `Failed to create entry: ${message}`,
        updateSuccess: 'Entry updated successfully',
        updateError: (message: string) => `Failed to update entry: ${message}`,
        deleteSuccess: 'Entry removed successfully',
        deleteError: (message: string) => `Failed to remove entry: ${message}`,
      },
    },
  },
  serviceTypeLabels: {
    LeadsOnline: 'LeadsOnline',
    LeadsOnlineCanada: 'LeadsOnline Canada',
    TexasOnlineMetals: 'Texas Online Metals',
    Kansas: 'Kansas',
    OhioDPS: 'Ohio DPS',
    Alabama: 'Alabama',
    CanadaBWI: 'Canada BWI',
    BCPolice: 'BC Police',
    WAPolice: 'WA Police',
    NMVTIS: 'NMVTIS',
  } as Record<string, string>,
  govReporting: {
    title: 'Gov Reporting',
    subtitle: 'Configure government reporting services',
    columns: {
      name: 'Name',
      serviceType: 'Service Type',
      uploadType: 'Upload Type',
      active: 'Active',
    },
    search: {
      placeholder: 'Search configurations...',
    },
    emptyState: {
      title: 'No reporting configurations',
      description:
        'Add government reporting service configurations to enable automated transaction reporting.',
    },
    actions: {
      addConfig: 'Add Configuration',
    },
    dialog: {
      createTitle: 'Add Reporting Configuration',
      editTitle: 'Edit Reporting Configuration',
      sections: {
        configuration: 'Configuration',
      },
      fields: {
        name: 'Name',
        serviceType: 'Service Type',
        uploadType: 'Upload Type',
        recyclerId: 'Recycler ID',
        toEmailAddress: 'Email Address',
        uploadTimezone: 'Upload Timezone',
        daysOfWeek: 'Days of Week',
        encryptedCredentials: 'Credentials',
        processBatchesAsUnit: 'Process Batches as Unit',
        isActive: 'Active',
      },
      uploadTypes: {
        Automatic: 'Automatic',
        Manual: 'Manual',
      },
      placeholders: {
        name: 'e.g., Texas Reporting',
        serviceType: 'Select service...',
        uploadType: 'Select upload type...',
        recyclerId: 'Optional recycler ID...',
        toEmailAddress: 'recipient@example.com',
        uploadTimezone: 'e.g., America/Chicago',
        encryptedCredentials: 'API key or password...',
      },
      actions: {
        create: 'Create Configuration',
        save: 'Save Changes',
      },
      messages: {
        createSuccess: 'Configuration created successfully',
        createError: (message: string) =>
          `Failed to create configuration: ${message}`,
        updateSuccess: 'Configuration updated successfully',
        updateError: (message: string) =>
          `Failed to update configuration: ${message}`,
        deleteSuccess: 'Configuration deleted successfully',
        deleteError: (message: string) =>
          `Failed to delete configuration: ${message}`,
      },
    },
    detail: {
      subtitle: 'Configuration details and schedule',
      sections: {
        configInfo: 'Configuration',
        schedule: 'Schedule',
        facilities: 'Linked Facilities',
        fieldSelection: 'Field Selection',
      },
      fields: {
        serviceType: 'Service Type',
        uploadType: 'Upload Type',
        recyclerId: 'Recycler ID',
        email: 'Email Address',
        timezone: 'Timezone',
        batchProcessing: 'Batch Processing',
        active: 'Active',
        created: 'Created',
        updated: 'Updated',
        daysOfWeek: 'Days of Week',
        credentials: 'Credentials',
      },
      facilityAssignment: {
        columns: {
          facility: 'Facility',
          reportingFacilityId: 'Reporting ID',
          mlid: 'MLID',
        },
        actions: {
          addFacility: 'Add Facility',
        },
        emptyState: {
          title: 'No facilities assigned',
          description:
            'Assign facilities to this reporting configuration to enable dispatch.',
        },
        dialog: {
          createTitle: 'Assign Facility',
          editTitle: 'Edit Facility Assignment',
          fields: {
            facility: 'Facility',
            reportingFacilityId: 'Reporting Facility ID',
            mlid: 'MLID',
          },
          placeholders: {
            facility: 'Select a facility...',
            reportingFacilityId: 'Optional reporting ID...',
            mlid: 'Optional MLID...',
          },
          actions: {
            create: 'Assign Facility',
            save: 'Save Changes',
          },
          messages: {
            createSuccess: 'Facility assigned successfully',
            createError: (message: string) =>
              `Failed to assign facility: ${message}`,
            updateSuccess: 'Facility assignment updated successfully',
            updateError: (message: string) =>
              `Failed to update assignment: ${message}`,
            deleteSuccess: 'Facility unassigned successfully',
            deleteError: (message: string) =>
              `Failed to unassign facility: ${message}`,
          },
        },
      },
      fieldSelection: {
        groups: {
          scaleTicket: 'Scale Ticket',
          publicSupplier: 'Public Supplier',
          vehicle: 'Vehicle',
          material: 'Material',
        },
        fields: {
          ticketNumber: 'Ticket Number',
          ticketDate: 'Ticket Date',
          grossWeight: 'Gross Weight',
          tareWeight: 'Tare Weight',
          netWeight: 'Net Weight',
          supplierName: 'Supplier Name',
          supplierAddress: 'Address',
          supplierPhone: 'Phone',
          supplierIdType: 'ID Type',
          supplierId: 'Supplier ID',
          vehicleMake: 'Make',
          vehicleModel: 'Model',
          vehicleYear: 'Year',
          vehiclePlate: 'Plate',
          vehicleVin: 'VIN',
          materialDescription: 'Description',
          materialCode: 'Code',
          materialQuantity: 'Quantity',
          materialUnit: 'Unit',
        },
      },
      dayLabels: {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun',
      },
      notFound: 'Configuration not found',
      actions: {
        backToList: 'Back to Configurations',
        viewDispatchHistory: 'View Dispatch History',
      },
    },
  },
  dispatchHistory: {
    title: 'Dispatch History',
    subtitle: 'Monitor government reporting dispatch records',
    columns: {
      facility: 'Facility',
      configuration: 'Configuration',
      serviceType: 'Service',
      status: 'Status',
      batchId: 'Batch ID',
      submittedAt: 'Submitted',
      errorMessage: 'Error',
      retries: 'Retries',
    },
    search: {
      placeholder: 'Search dispatch records...',
    },
    filters: {
      status: 'Status',
      configuration: 'Configuration',
      allStatuses: 'All Statuses',
      allConfigurations: 'All Configurations',
    },
    statuses: {
      Queued: 'Queued',
      Processing: 'Processing',
      Submitted: 'Submitted',
      Failed: 'Failed',
      NotReported: 'Not Reported',
    },
    emptyState: {
      title: 'No dispatch records',
      description:
        'Dispatch records will appear here once government reporting is configured and running.',
    },
    actions: {
      retry: 'Retry',
    },
    messages: {
      retrySuccess: 'Dispatch record queued for retry',
      retryError: (message: string) => `Failed to retry record: ${message}`,
    },
  },
  itemCodes: {
    title: 'Item Codes',
    subtitle: 'Manage government reporting classification codes per item',
    columns: {
      item: 'Item',
      serviceType: 'Service Type',
      code: 'Code',
      reportable: 'Reportable',
    },
    search: {
      placeholder: 'Search codes...',
    },
    emptyState: {
      title: 'No item jurisdiction codes',
      description:
        'Add jurisdiction codes to classify items for government reporting.',
    },
    actions: {
      addCode: 'Add Code',
    },
    dialog: {
      createTitle: 'Add Jurisdiction Code',
      editTitle: 'Edit Jurisdiction Code',
      sections: {
        jurisdictionCode: 'Jurisdiction Code',
      },
      fields: {
        item: 'Item',
        serviceType: 'Service Type',
        code: 'Code',
        isReportable: 'Reportable',
      },
      placeholders: {
        item: 'Select an item...',
        serviceType: 'Select service...',
        code: 'e.g., 0001',
      },
      actions: {
        create: 'Add Code',
        save: 'Save Changes',
      },
      messages: {
        createSuccess: 'Jurisdiction code created successfully',
        createError: (message: string) => `Failed to create code: ${message}`,
        updateSuccess: 'Code updated successfully',
        updateError: (message: string) => `Failed to update code: ${message}`,
        deleteSuccess: 'Code removed successfully',
        deleteError: (message: string) => `Failed to remove code: ${message}`,
      },
    },
  },
};

const preferencesView = {
  title: 'Preferences',
  language: {
    label: 'Language',
    description: 'Choose the language for the application interface',
  },
  moduleOrder: {
    title: 'Sidebar Module Order',
    description: 'Drag and drop to reorder modules in the sidebar',
    resetToDefault: 'Reset to default',
    setAsLandingPage: 'Set as default landing page',
    defaultLandingPage: 'Default landing page',
    landingPageSet: (moduleName: string) => `Default page set to ${moduleName}`,
    landingPageCleared: 'Default page cleared',
    messages: {
      saveError: 'Failed to save module order',
      landingPageError: 'Failed to set default landing page',
    },
  },
  messages: {
    saveSuccess: 'Language preference saved',
    saveError: 'Failed to save language preference',
  },
};

const notificationsView = {
  title: 'Notification Preferences',
  description:
    'Choose how you want to be notified for each type of event. Critical notifications cannot be disabled.',
  empty: 'No notification types configured',
  columns: {
    type: 'Notification Type',
  },
  channels: {
    in_app: 'In-App',
    email: 'Email',
    push: 'Push',
  } as Record<string, string>,
  categories: {
    operations: 'Operations',
    trade: 'Trade',
    finance: 'Finance',
    inventory: 'Inventory',
    logistics: 'Logistics',
    security: 'Security',
  } as Record<string, string>,
  typeLabels: {
    'booking.status_changed': 'Booking Status Changed',
    'load.shipped': 'Load Shipped',
    'load.reconciled': 'Load Reconciled',
    'load.voided': 'Load Voided',
    'load.received': 'Load Received',
    'purchase_order.voided': 'Purchase Order Voided',
    'purchase_order.closed': 'Purchase Order Closed',
    'purchase_load.received': 'Purchase Load Received',
    'purchase_load.voided': 'Purchase Load Voided',
    'invoice.paid': 'Invoice Paid',
    'invoice.voided': 'Invoice Voided',
    'bill.paid': 'Bill Paid',
    'bill.voided': 'Bill Voided',
    'task.failed': 'Task Failed',
    'task.assigned_to_driver': 'Task Assigned to Driver',
    'task.assigned_to_hauler': 'Task Assigned to Hauler',
    'route.failed': 'Route Failed',
    'route.completed': 'Route Completed',
    'route.cancelled': 'Route Cancelled',
    'user.added_to_organization': 'User Added to Organization',
    'user_role.assigned': 'Role Assigned',
    'sales_order.status_changed': 'Sales Order Status Changed',
  } as Record<string, string>,
  messages: {
    saveError: 'Failed to save notification preference',
    errorMessage: 'Failed to load notification preferences',
  },
  devSettings: {
    title: 'Developer Settings',
    selfNotify: 'Self-notify',
    selfNotifyDescription:
      'Receive notifications for your own actions. Useful for testing.',
  },
};

const customerPortal = {
  title: 'Customer Portal',
  subtitle: 'Manage customer access and permissions',
  tabs: {
    roles: 'Roles',
    users: 'Users',
  },
  search: {
    placeholder: 'Search',
  },
  emptyState: {
    roles: 'No roles found',
    users: 'No customers found',
  },
  actions: {
    addRole: 'Add Role',
    inviteCustomer: 'Invite Customer',
    discard: 'Discard',
    save: 'Save Updates',
    removeAccess: 'Remove Access',
    deleteAccount: 'Delete Account',
    cancelInvite: 'Cancel Invite',
  },
  userDetails: {
    tabs: {
      contactInfo: 'Contact Info',
      roles: 'Roles',
      locations: 'Locations',
    },
    activeSince: (date: string) => `Active since ${date}`,
    fields: {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      company: 'Company',
      contactLocation: 'Contact Location',
    },
    loading: 'Loading customer...',
    contactInfo: {
      sectionTitle: 'Contact Info',
      messages: {
        updateSuccess: 'Customer updated successfully',
        updateError: 'Failed to update customer',
      },
    },
    roles: {
      facilitiesAssigned: (count: number) =>
        `${count} ${count === 1 ? 'facility' : 'facilities'} assigned`,
      emptyState: {
        title: 'Select a role',
        subtitle: 'Choose a role from the list to assign facilities',
      },
      messages: {
        updateSuccess: 'Role facilities updated successfully',
        updateError: 'Failed to update role facilities',
      },
    },
    locations: {
      title: 'Locations',
      subtitle: 'Customer location access will be configured here',
    },
    messages: {
      accessRemoved: 'Access removed',
      accessRemoveError: 'Failed to remove access',
      accountDeleted: 'Account deleted',
      accountDeleteError: 'Failed to delete account',
    },
  },
  roles: {
    table: {
      columns: {
        name: 'Role Name',
        users: 'Users',
        status: 'Status',
        actions: 'Actions',
      },
      actions: {
        archiveRole: 'Archive role',
        restoreRole: 'Restore role',
      },
      aria: 'External roles table',
    },
    emptyState: {
      title: 'No roles found',
      subtitle: 'There are no external roles matching your search criteria.',
    },
    details: {
      tabs: {
        permissions: 'Permissions',
        users: 'Users',
      },
      actions: {
        rename: 'Rename',
        archive: 'Archive',
        restore: 'Restore',
      },
      users: {
        viewUserSettings: 'View User Settings',
        removeFromRole: 'Remove from Role',
        removeFromRoleDialog: {
          title: (name: string) => `Remove ${name} from this role?`,
          description: (name: string) =>
            `After confirming, ${name} will be removed from this role. This action can be undone by reassigning the role.`,
          confirm: 'Remove',
          cancel: 'Cancel',
          success: 'User removed from role',
          error: 'Failed to remove user from role',
        },
        bulk: {
          selected: 'selected',
          removeFromRole: 'Remove from Role',
          resendInvite: 'Resend Invite',
          bulkRemoveDialog: {
            title: (count: number) =>
              `Remove ${count} user${count === 1 ? '' : 's'} from this role?`,
            description: (count: number) =>
              `After confirming, ${count} user${count === 1 ? '' : 's'} will be removed from this role. This action can be undone by reassigning the role.`,
            confirm: 'Remove',
            cancel: 'Cancel',
          },
          removeSuccess: (count: number) =>
            `${count} user${count === 1 ? '' : 's'} removed from role`,
          removeError: 'Failed to remove users from role',
        },
      },
    },
    renameDialog: {
      title: 'Rename Role',
      label: 'Role Name',
      placeholder: 'Enter role name',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Role renamed successfully',
      error: 'Failed to rename role',
    },
    addRoleDialog: {
      title: 'Create Customer Role',
      fields: {
        name: {
          label: 'Role Name',
          placeholder: 'Enter role name',
        },
      },
      actions: {
        cancel: 'Cancel',
        save: 'Save Role',
      },
      messages: {
        success: 'Role created successfully',
        error: 'Failed to create role',
      },
    },
    dialogs: {
      archive: {
        title: (name: string) => `Archive ${name}?`,
        description: (name: string) =>
          `After confirming, the role "${name}" will be archived. Users assigned to this role will lose the associated permissions. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Archive',
      },
      restore: {
        title: (name: string) => `Restore ${name}?`,
        description: (name: string) =>
          `After confirming, the role "${name}" will be restored and become active again. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Restore',
      },
      bulkArchive: {
        title: (count: number) =>
          `Archive ${count} role${count === 1 ? '' : 's'}?`,
        description: (count: number) =>
          `After confirming, ${count} role${count === 1 ? '' : 's'} will be archived. Users assigned to these roles will lose the associated permissions. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Archive',
      },
      bulkRestore: {
        title: (count: number) =>
          `Restore ${count} role${count === 1 ? '' : 's'}?`,
        description: (count: number) =>
          `After confirming, ${count} role${count === 1 ? '' : 's'} will be restored and become active again. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Restore',
      },
    },
    bulk: {
      selected: 'selected',
      archive: 'Archive',
      restore: 'Restore',
    },
    addUsersDialog: {
      title: 'Add User(s) to {roleName} Role',
      description: 'Select users to add to this role',
      steps: {
        users: 'Users',
        facilities: 'Facilities',
      },
      users: {
        header: 'USERS',
        searchPlaceholder: 'Search',
      },
      facilities: {
        header: 'FACILITIES',
        searchPlaceholder: 'Search',
      },
      actions: {
        clear: 'Clear',
        next: 'Next',
        back: 'Back',
        save: 'Save',
        addUser: 'Add User ({count})',
      },
      messages: {
        success: 'Users added to role successfully',
        error: 'Failed to add users to role',
        partialSuccess:
          '{successCount} of {totalCount} users added successfully',
      },
    },
  },
  users: {
    selected: 'selected',
    table: {
      columns: {
        name: 'Name',
        contactInfo: 'Contact Info',
        status: 'Status',
      },
      aria: 'Customer portal users table',
    },
    actions: {
      cancelInvite: 'Cancel Invite',
      removeAccess: 'Remove Access',
      delete: 'Delete',
    },
    emptyState: {
      title: 'No customers found',
      subtitle: 'There are no customers matching your search criteria.',
    },
    dialogs: {
      cancelInvite: {
        title: (name: string) => `Cancel invite for ${name}?`,
        description: (name: string) =>
          `After confirming, the invitation for ${name} will be cancelled. Do you want to proceed?`,
        discard: 'Discard',
        confirm: 'Cancel Invite',
      },
      removeAccess: {
        title: (name: string) => `Remove access for ${name}?`,
        description: (name: string) =>
          `After confirming, ${name} will lose access to the customer portal. Do you want to proceed?`,
        discard: 'Discard',
        remove: 'Remove',
      },
      deleteAccount: {
        title: (name: string) => `Delete ${name}?`,
        description: (name: string) =>
          `After confirming, ${name} will be deleted and no longer have access. This action cannot be undone.`,
        discard: 'Discard',
        delete: 'Delete',
      },
    },
    messages: {
      invitationCancelled: 'Invitation cancelled',
      invitationCancelError: 'Failed to cancel invitation',
      accessRemoved: 'Access removed',
      accessRemoveError: 'Failed to remove access',
      customerRemoved: 'Customer removed',
      customerRemoveError: 'Failed to delete customer',
    },
  },
  userStatus: {
    active: 'Active',
    inviteSent: 'Invite Sent',
  },
  inviteCustomerDialog: {
    title: 'Invite Customer',
    fields: {
      account: {
        label: 'Account',
        placeholder: 'Select an account',
      },
      email: {
        label: 'Email',
        placeholder: 'Email address',
      },
    },
    actions: {
      cancel: 'Cancel',
      sendInvite: 'Send Invite',
    },
    messages: {
      success: 'Customer invitation sent successfully',
      error: 'Failed to send customer invitation',
    },
  },
};

const hardwareView = {
  sidebar: {
    title: 'Hardware',
  },
  sections: {
    sidecars: 'Sidecars',
    cameras: 'Cameras',
    scanners: 'Scanners',
    printers: 'Printers',
    scales: 'Scales',
    ustudio: 'U Studio',
  },
  routeLabels: {
    hardware: 'Hardware',
    registerSidecar: 'Register Sidecar',
    sidecarDetails: 'Sidecar Details',
    cameraDetails: 'Camera Details',
    scannerDetails: 'Scanner Details',
    printerDetails: 'Printer Details',
    scaleDetails: 'Scale Details',
  },
  common: {
    errorLabel: 'Error',
    unknownError: 'Unknown error',
    confirmDelete: 'Delete',
    confirmWipe: 'Wipe',
    cancel: 'Cancel',
  },
  sidecarsPanel: {
    columns: {
      name: 'Name',
      status: 'Status',
      operatingSystem: 'Operating System',
      facility: 'Facility',
      localIpAddress: 'Local IP',
      created: 'Created',
    },
    statusLabels: {
      Pending: 'Pending',
      Online: 'Online',
      Offline: 'Offline',
    },
    search: {
      placeholder: 'Search sidecars...',
      ariaLabel: 'Search sidecars',
    },
    emptyState: {
      title: 'No sidecars found',
      description:
        'Sidecars appear here after they are registered by the system.',
    },
    connectionLabels: {
      local: 'Local',
      relayed: 'Relayed',
    },
    actions: {},
    errors: {
      loadingFailed: (message: string) => `Error loading sidecars: ${message}`,
    },
    ariaLabel: 'Sidecars list',
  },
  sidecarDetailView: {
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
      disconnected: 'Disconnected',
      connecting: 'Connecting',
    },
    sections: {
      systemInfo: 'System Information',
      security: 'Security',
      location: 'Location',
      deviceDiscovery: 'Device Discovery',
      network: 'Network',
      timestamps: 'Timestamps',
    },
    fields: {
      name: 'Name',
      operatingSystem: 'Operating System',
      operatingSystemVersion: 'OS Version',
      version: 'Sidecar Version',
      publicKey: 'Public Key',
      facility: 'Facility',
      localIpAddress: 'Local IP Address',
      remoteIpAddressV4: 'Remote IPv4 Address',
      remoteIpAddressV6: 'Remote IPv6 Address',
      linkSpeedUpstream: 'Upload Speed',
      linkSpeedDownstream: 'Download Speed',
      latencyMs: 'Latency',
      jitterMs: 'Jitter',
      discoveryEnabled: 'Device Discovery',
      created: 'Created',
      updated: 'Updated',
      connectedAt: 'Connected At',
      lastHeartbeatAt: 'Last Heartbeat',
    },
    descriptions: {
      discoveryEnabled:
        'Automatically discover and register cameras, scanners, printers, and scales connected to this sidecar.',
    },
    placeholders: {
      name: 'e.g., PROD-SRV-01',
      operatingSystem: 'Select operating system...',
      operatingSystemVersion: 'e.g., Ubuntu 22.04',
      facility: 'Select facility...',
    },
    tabs: {
      details: 'Details',
      logs: 'Logs',
      uploadQueue: 'Upload Queue',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToHardware: 'Back to Hardware',
      downloadLatest: (version: string) => `Download v${version}`,
      checkForUpdate: 'Check for Update',
      checkingForUpdate: 'Checking...',
      syncCameras: 'Sync Cameras',
      syncScanners: 'Sync Scanners',
      syncPrinters: 'Sync Printers',
      syncScales: 'Sync Scales',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    units: {
      mbps: 'Mbps',
      ms: 'ms',
    },
    logs: {
      refresh: 'Refresh',
      wipeLogs: 'Clear Logs',
      wipeConfirmation:
        'Are you sure you want to wipe all logs? This action cannot be undone.',
      noLogs: 'No logs available',
      offline: 'Sidecar is offline. Connect to view logs.',
      streamErrorFallback: 'Failed to stream logs',
      wipeErrorFallback: 'Failed to wipe logs',
    },
    uploadQueue: {
      refresh: 'Refresh',
      delete: 'Delete',
      deleteConfirmation: 'Are you sure you want to delete this queue entry?',
      empty: 'Upload queue is empty',
      offline: 'Sidecar is offline. Connect to view queue.',
      columns: {
        id: 'ID',
        cameraId: 'Camera ID',
        storageKey: 'Storage Key',
        contentType: 'Content Type',
        fileSize: 'File Size',
        enqueuedAt: 'Enqueued At',
      },
      errors: {
        listEntriesFallback: 'Failed to list queue entries',
        deleteEntryFallback: 'Failed to delete entry',
      },
      fileSizeUnits: {
        bytes: 'B',
        kilobytes: 'KB',
        megabytes: 'MB',
      },
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this sidecar? This action cannot be undone.',
      deleteSuccess: 'Sidecar deleted successfully',
      deleteError: (message: string) => `Failed to delete sidecar: ${message}`,
      sidecarNotFound: 'Sidecar not found',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Sidecar updated successfully',
      updateError: (message: string) => `Failed to update sidecar: ${message}`,
      updateCheckTriggered: 'Update check triggered',
      updateCheckError: (message: string) =>
        `Failed to trigger update check: ${message}`,
      syncFailed: (error: string) => `Sync failed: ${error}`,
    },
  },
  camerasPanel: {
    columns: {
      name: 'Name',
      driverType: 'Driver',
      ipAddress: 'IP Address',
      status: 'Status',
      sidecar: 'Sidecar',
      created: 'Created',
    },
    statusLabels: {
      [enumCameraStatus.Discovered]: 'Discovered',
      [enumCameraStatus.Configured]: 'Configured',
    },
    phaseLabels: {
      discovered: 'Discovered',
      provisioning: 'Provisioning',
      wrong_credentials: 'Wrong Credentials',
      operational: 'Operational',
    },
    search: {
      placeholder: 'Search cameras...',
      ariaLabel: 'Search cameras',
    },
    emptyState: {
      title: 'No cameras found',
      description:
        'Cameras appear here after they are discovered by a sidecar.',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
    },
    actions: {
      addRtspCamera: 'Add RTSP Camera',
    },
    errors: {
      loadingFailed: (message: string) => `Error loading cameras: ${message}`,
    },
    ariaLabel: 'Cameras list',
  },
  scannersPanel: {
    columns: {
      name: 'Name',
      driverType: 'Driver',
      status: 'Status',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      [enumScannerStatus.Discovered]: 'Discovered',
    },
    search: {
      placeholder: 'Search scanners...',
      ariaLabel: 'Search scanners',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
    },
    emptyState: {
      title: 'No scanners found',
      description: 'Scanners appear here after they are detected by a sidecar.',
    },
    ariaLabel: 'Scanners list',
  },
  printersPanel: {
    columns: {
      name: 'Name',
      printerType: 'Type',
      driverType: 'Driver',
      status: 'Status',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      [enumPrinterStatus.Discovered]: 'Discovered',
    },
    search: {
      placeholder: 'Search printers...',
      ariaLabel: 'Search printers',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
    },
    emptyState: {
      title: 'No printers found',
      description: 'Printers appear here after they are detected by a sidecar.',
    },
    ariaLabel: 'Printers list',
  },
  scalesPanel: {
    columns: {
      name: 'Name',
      formatType: 'Format',
      connectionType: 'Connection',
      status: 'Status',
      sidecar: 'Sidecar',
    },
    statusLabels: {
      Discovered: 'Discovered',
      Configured: 'Configured',
      Active: 'Active',
    },
    search: {
      placeholder: 'Search scales...',
      ariaLabel: 'Search scales',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
    },
    emptyState: {
      title: 'No scales found',
      description: 'Scales appear here after they are configured on a sidecar.',
    },
    actions: {
      addScale: 'Add Scale',
    },
    ariaLabel: 'Scales list',
  },
  scaleCreateDialog: {
    title: 'Add Scale',
    fields: {
      name: 'Scale Name',
      connectionType: 'Connection Type',
      connectionTypeOptions: {
        TCP: 'TCP',
        Serial: 'Serial (USB)',
      },
      tcpHost: 'TCP Host',
      tcpHostPlaceholder: '192.168.1.100',
      tcpPort: 'TCP Port',
      tcpPortPlaceholder: '4001',
      serialPortPath: 'Serial Port',
      serialPortPathPlaceholder: '/dev/ttyUSB0',
      baudRate: 'Baud Rate',
      dataBits: 'Data Bits',
      stopBits: 'Stop Bits',
      parity: 'Parity',
      scaleFormatType: 'Scale Format',
      scaleFormatTypePlaceholder: 'Toledo',
      measurementMode: 'Measurement Mode',
      measurementModeOptions: {
        Continuous: 'Continuous',
        OnDemand: 'On Demand',
      },
      onDemandString: 'On Demand String',
      unitOfMeasurement: 'Unit of Measurement',
      unitOfMeasurementPlaceholder: 'Auto-detect from scale',
      unitOptions: {
        lb: 'Pounds (lb)',
        kg: 'Kilograms (kg)',
      },
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Select a sidecar...',
      camera: 'Camera',
      cameraPlaceholder: 'Select a camera (optional)...',
    },
    actions: {
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
    },
    messages: {
      success: 'Scale created successfully',
      error: (message: string) => `Failed to create scale: ${message}`,
    },
  },
  scaleDetailView: {
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
      disconnected: 'Disconnected',
      connecting: 'Connecting',
    },
    statusLabels: {
      Discovered: 'Discovered',
      Configured: 'Configured',
      Active: 'Active',
    },
    sections: {
      scaleInfo: 'Scale Information',
      configuration: 'Scale Configuration',
      connectionSettings: 'Connection Settings',
      associatedCamera: 'Associated Camera',
      connection: 'Connection',
      timestamps: 'Timestamps',
      liveWeight: 'Live Weight',
    },
    fields: {
      name: 'Name',
      connectionType: 'Connection Type',
      scaleFormatType: 'Scale Format',
      measurementMode: 'Measurement Mode',
      serialPortPath: 'Serial Port Path',
      baudRate: 'Baud Rate',
      dataBits: 'Data Bits',
      stopBits: 'Stop Bits',
      parity: 'Parity',
      tcpHost: 'TCP Host',
      tcpPort: 'TCP Port',
      onDemandString: 'On Demand String',
      unitOfMeasurement: 'Unit of Measurement',
      camera: 'Camera',
      cameraPlaceholder: 'Select a camera...',
      noCamera: 'None',
      sidecar: 'Sidecar',
      lastSeenAt: 'Last Seen',
      created: 'Created',
      updated: 'Updated',
      grossWeight: 'Gross Weight',
      tareWeight: 'Tare Weight',
      netWeight: 'Net Weight',
      stability: 'Stability',
    },
    labels: {
      stable: 'Stable',
      unstable: 'Unstable',
    },
    tabs: {
      info: 'Info',
      weight: 'Weight',
    },
    actions: {
      backToHardware: 'Back to Hardware',
      refreshWeight: 'Refresh Weight',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleting: 'Deleting...',
    },
    messages: {
      scaleNotFound: 'Scale not found',
      deleteConfirmation:
        'Are you sure you want to delete this scale? This action cannot be undone.',
      deleteSuccess: 'Scale deleted successfully',
      deleteError: (message: string) => `Failed to delete scale: ${message}`,
      offline: 'Scale is offline',
      connecting: 'Connecting to sidecar...',
      noWeightYet: 'No weight yet',
      weightReadFailed: 'Failed to read current weight',
      weightStreamFailed: 'Failed to subscribe to weight updates',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Scale updated successfully',
      updateError: (message: string) => `Failed to update scale: ${message}`,
    },
  },
  cameraDetailView: {
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
      disconnected: 'Disconnected',
      connecting: 'Connecting',
    },
    sections: {
      cameraInfo: 'Camera Information',
      configuration: 'Camera Configuration',
      deviceInfo: 'Device Information',
      network: 'Network',
      setup: 'Setup',
      initialization: 'Initialization',
      credentials: 'Credentials',
      stream: 'Stream',
      timestamps: 'Timestamps',
    },
    fields: {
      name: 'Name',
      uniqueId: 'Unique ID',
      driverType: 'Driver Type',
      ipAddress: 'IP Address',
      manufacturer: 'Manufacturer',
      model: 'Model',
      firmwareVersion: 'Firmware Version',
      status: 'Status',
      lastSeenAt: 'Last Seen',
      sidecar: 'Sidecar',
      username: 'Username',
      password: 'Password',
      created: 'Created',
      updated: 'Updated',
      rtspStatus: 'RTSP Status',
      rtspPort: 'RTSP Port',
      httpStatus: 'HTTP Status',
      httpPort: 'HTTP Port',
      onvifStatus: 'ONVIF Status',
      onvifPort: 'ONVIF Port',
      streamProfile: 'Stream Profile',
      streamUrl: 'Stream URL',
      snapshotUrl: 'Snapshot URL',
    },
    tabs: {
      details: 'Details',
      liveFeed: 'Live Feed',
    },
    actions: {
      delete: 'Delete',
      deleting: 'Deleting...',
      backToHardware: 'Back to Hardware',
      setCredentials: 'Set Credentials',
      settingCredentials: 'Setting...',
      initialize: 'Initialize Camera',
      initializing: 'Initializing...',
      enableHttp: 'Enable HTTP',
      enablingHttp: 'Enabling...',
      enableRtsp: 'Enable RTSP',
      enablingRtsp: 'Enabling...',
      enableOnvif: 'Enable ONVIF',
      enablingOnvif: 'Enabling...',
      cancel: 'Cancel',
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving...',
    },
    configuration: {
      profileSub: 'Real-time (Sub Stream)',
      profileMain: 'High Resolution (Main Stream)',
    },
    liveFeed: {
      connecting: 'Connecting to sidecar...',
      connectToSidecar: 'Connect to the sidecar to view live feed',
      cameraOffline: 'Camera is offline',
      noLiveFeedSupport: 'Camera does not support live feed',
      startingStream: 'Starting stream...',
      streamStartFailed: 'Failed to start stream',
      loadingSnapshot: 'Loading snapshot...',
      streamProfile: 'Stream Quality',
      profileMain: 'High Resolution (transcoded, ~2s latency)',
      profileSub: 'Real-time (896x512)',
      webrtcConnectionFailed:
        'Live video requires a direct network connection. PTZ and snapshots are still available.',
      snapshotFallback: 'Showing snapshot preview',
    },
    ptz: {
      title: 'PTZ Controls',
      up: 'Up',
      down: 'Down',
      left: 'Left',
      right: 'Right',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
    },
    floodlight: {
      title: 'Floodlight',
      on: 'Turn On',
      off: 'Turn Off',
      brightness: 'Brightness',
      brightnessValue: (value: number) => `${value}%`,
    },
    siren: {
      title: 'Siren',
      trigger: 'Trigger Siren',
      confirmTitle: 'Trigger Siren?',
      confirmMessage: 'This will activate the camera siren alarm.',
      confirm: 'Trigger',
      cancel: 'Cancel',
    },
    snapshot: {
      title: 'Snapshot',
      overlayEditorTitle: 'Snapshot Overlays',
      capture: 'Take Snapshot',
      capturing: 'Capturing...',
      addTextLayer: 'Add Text Layer',
      addImageLayer: 'Add Image Layer',
      removeLayer: 'Remove Layer',
      moveLayerUp: 'Move Layer Up',
      moveLayerDown: 'Move Layer Down',
      noLayers: 'No overlay layers configured.',
      layerTypeLabel: 'Layer Type',
      anchorLabel: 'Anchor',
      textLabel: 'Text',
      fontSizeLabel: 'Font Size',
      colorLabel: 'Color',
      uploadImageLabel: 'Overlay Image',
      clearImage: 'Clear Image',
      imageDataUrlLabel: 'Image Data URL',
      imageDataUrlPlaceholder:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...',
      download: 'Download',
      dismiss: 'Dismiss',
      channel: (channel: number) => `Channel ${channel}`,
      imageAlt: (channel: number) => `Snapshot channel ${channel}`,
      captureErrorFallback: 'Failed to capture snapshot',
      layerTypes: {
        text: 'Text',
        image: 'Image',
      },
      anchors: {
        topLeft: 'Top Left',
        topRight: 'Top Right',
        bottomLeft: 'Bottom Left',
        bottomRight: 'Bottom Right',
        fullFrame: 'Full Frame',
      },
      errors: {
        emptyTextLayer: 'Text overlay layers require text.',
        invalidImageDataUrl:
          'Image overlay layers require a valid base64 data URL.',
        unsupportedImageType:
          'Image overlays support PNG and JPEG data URLs only.',
      },
    },
    messages: {
      deleteConfirmation:
        'Are you sure you want to delete this camera? This action cannot be undone.',
      deleteSuccess: 'Camera deleted successfully',
      deleteError: (message: string) => `Failed to delete camera: ${message}`,
      cameraNotFound: 'Camera not found',
      credentialsSuccess: 'Credentials updated successfully',
      credentialsError: (message: string) =>
        `Failed to set credentials: ${message}`,
      initializationRequired:
        'This camera is in factory-default state. Initialize it to set a secure password and enable RTSP streaming.',
      sidecarNotConnected:
        'Sidecar is not connected. Initialization requires an active connection to the sidecar.',
      initializeSuccess: 'Camera initialized successfully',
      initializeError: (message: string) =>
        `Failed to initialize camera: ${message}`,
      enableHttpSuccess: 'HTTP enabled successfully',
      enableHttpError: (message: string) => `Failed to enable HTTP: ${message}`,
      enableRtspSuccess: 'RTSP enabled successfully',
      enableRtspError: (message: string) => `Failed to enable RTSP: ${message}`,
      enableOnvifSuccess: 'ONVIF enabled successfully',
      enableOnvifError: (message: string) =>
        `Failed to enable ONVIF: ${message}`,
      streamProfileSuccess: 'Stream profile updated successfully',
      streamProfileError: (message: string) =>
        `Failed to update stream profile: ${message}`,
      streamUpdateSuccess: 'Stream URLs updated successfully',
      streamUpdateError: (message: string) =>
        `Failed to update stream URLs: ${message}`,
      wrongCredentialsWarning:
        'Camera credentials are incorrect. Update credentials to reconnect.',
      wrongCredentialsLiveFeed:
        'Camera credentials are incorrect. Update credentials in the Details tab.',
      cameraSetupInProgress:
        'Camera is being set up. Live feed will be available once configuration is complete.',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    httpStatus: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    rtspStatus: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    onvifStatus: {
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Camera updated successfully',
      updateError: (message: string) => `Failed to update camera: ${message}`,
    },
  },
  demoCreateDialog: {
    titles: {
      scale: 'Add Demo Scale',
      printer: 'Add Demo Printer',
      scanner: 'Add Demo Scanner',
      camera: 'Add Demo Camera',
    },
    fields: {
      name: 'Name',
      printerType: 'Printer Type',
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Select a sidecar...',
    },
    printerTypeOptions: {
      Standard: 'Standard',
      Thermal: 'Thermal',
    },
    actions: {
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
      addDemoScale: 'Add Demo Scale',
      addDemoPrinter: 'Add Demo Printer',
      addDemoScanner: 'Add Demo Scanner',
      addDemoCamera: 'Add Demo Camera',
    },
    messages: {
      success: 'Demo device created successfully',
      error: (message: string) => `Failed to create demo device: ${message}`,
    },
  },
  rtspCameraCreateDialog: {
    title: 'Add RTSP Camera',
    fields: {
      name: 'Camera Name',
      streamUrl: 'Stream URL',
      streamUrlPlaceholder: 'rtsp://192.168.1.100:554/stream',
      snapshotUrl: 'Snapshot URL (optional)',
      snapshotUrlPlaceholder: 'rtsp://192.168.1.100:554/main',
      sidecar: 'Sidecar',
      sidecarPlaceholder: 'Select a sidecar...',
    },
    actions: {
      cancel: 'Cancel',
      create: 'Create',
      creating: 'Creating...',
    },
    messages: {
      success: 'RTSP camera created successfully',
      error: (message: string) => `Failed to create camera: ${message}`,
    },
  },
  scannerDetailView: {
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
      disconnected: 'Disconnected',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
      needsSetup: 'Needs Setup',
    },
    sections: {
      scannerInfo: 'Scanner Information',
      configuration: 'Scanner Configuration',
      deviceInfo: 'Device Information',
      connection: 'Connection',
      timestamps: 'Timestamps',
      scan: 'Scan',
      scanResult: 'Scan Result',
      images: 'Images',
      aamvaFields: 'AAMVA Fields',
    },
    formats: {
      aamva: 'AAMVA',
      raw: 'RAW',
    },
    fields: {
      name: 'Name',
      scannerId: 'Scanner ID',
      uniqueId: 'Unique ID',
      driverType: 'Driver Type',
      manufacturer: 'Manufacturer',
      model: 'Model',
      firmwareVersion: 'Firmware Version',
      status: 'Status',
      sidecar: 'Sidecar',
      created: 'Created',
      updated: 'Updated',
      format: 'Format',
      timeout: 'Timeout',
      elapsedMs: 'Elapsed (ms)',
    },
    scanResultLabels: {
      rawPayload: 'Raw payload',
      pdf417Payload: 'PDF417 payload',
      pdf417Decoded: 'PDF417 decoded',
      frontImageLabel: 'Front image label',
      backImageLabel: 'Back image label',
      portraitImageLabel: 'Portrait image label',
      parsedFields: (count: number) => `Parsed fields (${count})`,
      noParsedFields: 'No parsed fields',
      ocrText: 'OCR text',
      diagnostics: 'Diagnostics',
      yes: 'yes',
      no: 'no',
      bytes: (count: number) => `${count} bytes`,
      sourceLabels: {
        pdf417: 'PDF417',
        ocr: 'OCR',
      },
    },
    tabs: {
      info: 'Info',
      test: 'Test',
    },
    actions: {
      backToHardware: 'Back to Hardware',
      scanNow: 'Scan Now',
      scanning: 'Scanning...',
      download: 'Download',
      delete: 'Delete',
      deleting: 'Deleting...',
    },
    messages: {
      scannerNotFound: 'Scanner not found',
      unknownScanError: 'Unknown scan error',
      deleteConfirmation:
        'Are you sure you want to delete this scanner? This action cannot be undone.',
      deleteSuccess: 'Scanner deleted successfully',
      deleteError: (message: string) => `Failed to delete scanner: ${message}`,
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Scanner updated successfully',
      updateError: (message: string) => `Failed to update scanner: ${message}`,
    },
  },
  printerDetailView: {
    connectionLabels: {
      direct: 'Direct',
      relayed: 'Relayed',
      disconnected: 'Disconnected',
    },
    presenceLabels: {
      online: 'Online',
      offline: 'Offline',
    },
    sections: {
      printerInfo: 'Printer Information',
      configuration: 'Printer Configuration',
      deviceInfo: 'Device Information',
      connection: 'Connection',
      timestamps: 'Timestamps',
    },
    fields: {
      name: 'Name',
      printerId: 'Printer ID',
      uniqueId: 'Unique ID',
      driverType: 'Driver Type',
      manufacturer: 'Manufacturer',
      model: 'Model',
      firmwareVersion: 'Firmware Version',
      printerType: 'Printer Type',
      pageSize: 'Page Size',
      rollWidth: 'Roll Width',
      status: 'Status',
      sidecar: 'Sidecar',
      created: 'Created',
      updated: 'Updated',
    },
    printerTypeLabels: {
      Standard: 'Standard',
      Thermal: 'Thermal',
    },
    pageSizeLabels: {
      Letter: 'Letter',
      A4: 'A4',
      Legal: 'Legal',
    },
    rollWidthLabels: {
      Roll58mm: '58mm',
      Roll80mm: '80mm',
    },
    queueColumns: {
      documentName: 'Document',
      status: 'Status',
      pages: 'Pages',
      size: 'Size',
    },
    tabs: {
      info: 'Info',
      queue: 'Print Queue',
    },
    actions: {
      backToHardware: 'Back to Hardware',
      printTestPage: 'Print Test Page',
      cancelJob: 'Cancel',
      refresh: 'Refresh',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      deleting: 'Deleting...',
    },
    messages: {
      printerNotFound: 'Printer not found',
      testPageSent: 'Test page sent to printer',
      jobCancelled: 'Print job cancelled',
      queueEmpty: 'No print jobs in queue',
      notConnected: 'Not connected to sidecar',
      deleteConfirmation:
        'Are you sure you want to delete this printer? This action cannot be undone.',
      deleteSuccess: 'Printer deleted successfully',
      deleteError: (message: string) => `Failed to delete printer: ${message}`,
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to discard them?',
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    toast: {
      discarded: 'Changes discarded',
      updated: 'Printer updated successfully',
      updateError: (message: string) => `Failed to update printer: ${message}`,
    },
  },
  sidecarRegisterView: {
    title: 'Register Sidecar',
    subtitle: 'Complete the registration for a new sidecar device.',
    sections: {
      deviceInformation: 'Device Information',
      configuration: 'Configuration',
    },
    fields: {
      sidecarId: 'Sidecar ID',
      publicKey: 'Public Key',
      facility: 'Facility',
      name: 'Name',
    },
    placeholders: {
      facility: 'Select a facility...',
      name: 'e.g., PROD-SRV-01',
    },
    actions: {
      register: 'Register Sidecar',
      registering: 'Registering...',
      backToHardware: 'Back to Hardware',
    },
    messages: {
      missingParams:
        'Missing registration parameters. Please start the registration from the sidecar terminal.',
      success: 'Sidecar registered successfully. You can close this page.',
      error: (message: string) => `Registration failed: ${message}`,
    },
  },
};

const ustudioView = {
  preferences: {
    title: 'Preferences',
    enabled: 'Enable U Studio',
    brightness: 'Brightness',
    save: 'Save',
    discard: 'Discard',
  },
  debug: {
    title: 'Debug',
    connect: 'Connect',
    disconnect: 'Disconnect',
    status: 'Status',
    deviceInfo: 'Device Info',
    eventLog: 'Event Log',
    buttons: 'buttons',
    framebuffer: 'framebuffer',
    noEvents: 'No events yet',
    webUsbUnavailable:
      'WebUSB is not available in this browser. Use Chrome or another Chromium-based browser.',
  },
  status: {
    disconnected: 'Disconnected',
    connecting: 'Connecting',
    connected: 'Connected',
    error: 'Error',
  },
};

const automationsView = {
  title: 'Automations',
  fields: {
    executionTimeout: 'Execution Timeout (seconds)',
    executionTimeoutDescription:
      'Maximum time in seconds an automation execution can run before being cancelled.',
    maxRetryCount: 'Max Retry Count',
    maxRetryCountDescription:
      'Maximum number of times a failed action will be retried.',
  },
  comingSoon: 'Automation settings are coming soon.',
  actions: {
    save: 'Save Changes',
  },
};

const settingsLayout = {
  title: 'Settings',
  search: 'Search',
  notImplemented: 'Not implemented yet',
  groupTitles: {
    personal: 'Personal',
    organization: 'Organization',
    modules: 'Modules',
    compliance: 'Compliance',
  },
  routeLabels: {
    profile: 'Profile',
    accessAndSecurity: 'Access & Security',
    preferences: 'Preferences',
    notifications: 'Notifications',
    locations: 'Locations',
    facilityDetails: 'Facility Details',
    groupDetails: 'Group Details',
    teamAndPermissions: 'Team & Permissions',
    userDetails: 'User Details',
    roleDetails: 'Role Details',
    inventory: 'Inventory',
    itemDetails: 'Item Details',
    itemGroupDetails: 'Item Group Details',
    itemAliasDetails: 'Item Alias Details',
    categoryDetails: 'Category Details',
    hardware: 'Hardware',
    registerSidecar: 'Register Sidecar',
    sidecarDetails: 'Sidecar Details',
    cameraDetails: 'Camera Details',
    scannerDetails: 'Scanner Details',
    printerDetails: 'Printer Details',
    scaleDetails: 'Scale Details',
    sales: 'Sales',
    finance: 'Finance',
    ruleConfiguration: 'Rule Configuration',
    facilityRules: 'Facility Rules',
    doNotBuyList: 'Do-Not-Buy List',
    govReporting: 'Gov Reporting',
    configDetail: 'Config Detail',
    itemCodes: 'Item Codes',
    dispatchHistory: 'Dispatch History',
    customAttributes: 'Custom Attributes',
    customerPortal: 'Customer Portal',
    customerDetails: 'Customer Details',
  },
};

export const en = {
  automationsView,
  complianceView,
  customerPortal,
  customAttributesView,
  hardwareView,
  inventoryView,
  jsonView,
  notificationsView,
  preferencesView,
  settingsLayout,
  settingsView,
  teamAndPermissions,
  ustudioView,
};
